import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useApi from "../../hooks/useApi";
import { useModalStore } from "../../store/GlobalVarriables";

import Loader from "../../Components/Loader";
import GlobalActionButtons from "../../Components/GlobalActionButtons/GlobalActionButtons";
import ModalConfirmation from "../../Components/ModalConfirmation/ModalConfirmation";
import ModalWarning from "../../Components/ModalWarning/ModalWarning";
import DotsMenu from "../../Components/DotsMenu";
import Weekdays from "../../Components/Weekdays/Weekdays";
import PlayerDetails from "../../Components/PlayerDetails/PlayerDetails";

import { ReactComponent as Tick } from "../../static/images/tick_sign.svg";
import { ReactComponent as DuePayment } from "../../static/images/due_payment.svg";
import { ReactComponent as PayerBadge } from "../../static/images/payer_badge.svg";

import "./style.scss";

const SessionDetails = () => {
  const { get, del, put, patch, post, loading } = useApi();

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { scheduleDayId, eventId, eventDate, originalEventDate } =
    location.state || {};

  const invRef = useRef(null);
  const subRef = useRef(null);

  const { sessionID } = useParams();

  const [scheduleDetail, setScheduleDetail] = useState([]);
  const [invitationData, setInvitationData] = useState([]);
  const [subDetails, setSubDetails] = useState([]);

  const [invitationInputToggle, setInvitationInputToggle] = useModalStore("");

  const [confirmModalToggle, setConfirmModalToggle] = useState("");
  const [modalText, setModalText] = useState("");
  const [modalSubText, setModalSubText] = useState("");
  const [modalValue, setModalValue] = useState(0); // for detect what action is needed

  const [warningModalToggle, setWarningModalToggle] = useState("");
  const [warningModalText, setWarningModalText] = useState("");
  const [warningButtonText, setWarningButtonText] = useState("");

  const [detailMenuToggle, setDetailMenuToggle] = useState(false);

  const [playerDetails, setPlayerDetails] = useState([]);
  const [playerDetailsToggle, setPlayerDetailsToggle] = useState(false);

  const [scheduleId, setScheduleId] = useState("");
  const [groupPayerId, setGroupPayerId] = useState("");
  const [currentPayer, setCurrentPayer] = useState([]);
  const [payerIdentifier, setPayerIdentifier] = useState([]);

  const menuItems = [
    {
      text: t("schedule_details"),
      onClick: () => {
        navigate(`/schedule/${sessionID}`);
      },
    },
    {
      text: t("invite_customer"),
      disabled: scheduleDetail.availableSeats <= 0,
      onClick: () => {
        handleValidations();
      },
    },
    {
      text: t("cancel_session"),
      onClick: () => handleMenuButton("cancel session"),
    },
    {
      text: t("reschedule_session"),
      onClick: () => {
        navigate(`/reschedule/${sessionID}`, {
          state: {
            eventDate,
            originalEventDate,
            scheduleDayId,
            eventId,
          },
        });
      },
    },
  ];

  const fetchDetails = async () => {
    try {
      const data = await get(`/Schedule/${sessionID}`);
      setScheduleDetail(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchInvitation = async () => {
    try {
      const data = await get(`/Schedule/${sessionID}/Invitations`);
      if (data) {
        const initializedInvitationData = data.map((item) => ({
          phoneNumber: item.invitedPhoneNumber,
          email: item.invitedEmailAddress,
          consumerName: item.consumerName,
          status: item.status,
          id: item.id,
          showMenu: false,
        }));

        setInvitationData(initializedInvitationData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSub = async () => {
    try {
      const data = await get(`/Schedule/${sessionID}/Subscriptions`);

      if (data) {
        const initializedSubscriptionData = data.map((item) => ({
          businessAccountName: item.businessAccount.name,
          phoneNumber: item.businessAccount.contactMSISDN,
          email: item.businessAccount.contactEmail,
          overduePayment: item.overduePayment,
          id: item.id,
          isGroupPayer: item.isGroupPayer,
          showMenu: false,
        }));
        setSubDetails(initializedSubscriptionData);
        setCurrentPayer(data.filter((item) => item.isGroupPayer === true));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const cancelSession = async () => {
    try {
      const method = eventId ? patch : post;

      const dataToPass = eventId
        ? {
            id: eventId,
            eventStatus: "Cancelled",
          }
        : {
            scheduleDayId: scheduleDayId,
            eventTime: eventDate.toISOString(),
            originalEventTime: originalEventDate.toISOString(),
            eventStatus: "Cancelled",
            scheduleId: sessionID,
          };

      const data = await method("/ApplicationEvent", dataToPass);
      if (data) {
        setWarningModalToggle("modal-warning");
        setWarningModalText(t("session_cancel_success"));
        setWarningButtonText(t("go_main_page"));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleGroupPayer = async () => {
    if (currentPayer.length) {
      try {
        const data = await patch(`/Subscription`, {
          id: currentPayer[0].id,
          isGroupPayer: false,
        });
      } catch (error) {}
    }

    try {
      const data = await patch(`/Subscription`, {
        id: groupPayerId,
        isGroupPayer: true,
        // description: "string",
      });

      await fetchSub();
    } catch (error) {
      console.log(error);
    }
  };

  const getDataId = (id) => {
    setScheduleId(id);
  };

  const postInvitation = async () => {
    if (modalValue === 1) {
      cancelSession();
    } else if (modalValue === 2) {
      try {
        const data = await put(`/Invitation`, {
          id: scheduleId,
          operation: "Cancel",
        });

        setWarningModalToggle("modal-warning");
        setWarningModalText(t("cancel_success"));
        setWarningButtonText(t("close"));
      } catch (error) {
        console.log(error);
      }
    } else if (modalValue === 3) {
      try {
        const data = await del(`/Subscription/${scheduleId}/Cancel`);
        setWarningModalToggle("modal-warning");
        setWarningModalText(t("cancel_success"));
        setWarningButtonText(t("close"));
      } catch (error) {
        console.log(error);
      }
    }
  };

  const handleValidations = () => {
    /// disappear invitation menu
    setInvitationData(
      invitationData.map((item, i) => ({
        ...item,
        showMenu: false,
      })),
    );

    /// disappear subscription menu
    setSubDetails(
      subDetails.map((item, i) => ({
        ...item,
        showMenu: false,
      })),
    );

    if (scheduleDetail.availableSeats > 0) {
      setInvitationInputToggle("invite");
      setDetailMenuToggle(false);

      navigate(`/schedule/${sessionID}`);
    }
  };

  const handleMenuButton = async (identity, payerItem) => {
    setDetailMenuToggle(false);

    if (identity === "cancel session") {
      setConfirmModalToggle("modal-confirm");
      setModalText(
        t("cancel_session_confirmation_modal_text", {
          date: convertDate(eventDate),
        }),
      );
      setModalSubText(t("cancel_session_confirmation_modal_subText"));
      setModalValue(1);
    } else if (identity === "cancel invitation") {
      setConfirmModalToggle("modal-confirm");
      setModalText(t("cancel_invitation_confirmation_modal_text"));
      setModalSubText(t("cancel_invitation_confirmation_modal_subText"));

      setWarningModalText(t("cancel_success"));
      setWarningButtonText(t("close"));
      setModalValue(2);
      /// disappear invitation menu
      setInvitationData(
        invitationData.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );
    } else if (identity === "cancel subscription") {
      setConfirmModalToggle("modal-confirm");
      setModalText(t("cancel_subscription_confirmation_modal_text"));
      setModalSubText(t("cancel_subscription_confirmation_modal_subText"));

      setWarningModalText(t("cancel_success"));
      setWarningButtonText(t("close"));
      setModalValue(3);

      /// disappear subscription menu
      setSubDetails(
        subDetails.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );
    } else if (identity === "select as group payer") {
      if (payerIdentifier.isGroupPayer === true) {
        return;
      }

      setSubDetails(
        subDetails.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );

      setConfirmModalToggle("modal-confirm");
      setModalText(
        t("add_into_group_confirmation_modal_text", {
          name: payerItem.businessAccountName,
          scheduleName: scheduleDetail.description.trim(""),
        }),
      );
      setModalSubText(t("add_into_group_confirmation_modal_subText"));
      setModalValue(4);
      setGroupPayerId(payerItem.id);
    } else if (identity === "player details") {
      setPlayerDetails(payerItem);
      setPlayerDetailsToggle(true);

      /// disappear subscription menu

      setSubDetails(
        subDetails.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );

      /// disappear invitation menu
      setInvitationData(
        invitationData.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );
    }
  };

  const toggleMenu = (identity, index) => {
    if (identity === "invitation") {
      setInvitationData(
        invitationData.map((item, i) =>
          i === index
            ? { ...item, showMenu: !item.showMenu }
            : { ...item, showMenu: false },
        ),
      );

      /// disappear subscription menu
      setSubDetails(
        subDetails.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );
    } else {
      setSubDetails(
        subDetails.map((item, i) =>
          i === index
            ? { ...item, showMenu: !item.showMenu }
            : { ...item, showMenu: false },
        ),
      );
      /// disappear invitation menu
      setInvitationData(
        invitationData.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );
    }
  };

  const handleToggleDetailsMenu = () => {
    setDetailMenuToggle((prevState) => !prevState);
  };

  const handleClickOutside = (event) => {
    if (invRef.current && !invRef.current.contains(event.target)) {
      setInvitationData(
        invitationData.map((item) => ({
          ...item,
          showMenu: false,
        })),
      );
    }
    if (subRef.current && !subRef.current.contains(event.target)) {
      setSubDetails(
        subDetails.map((item) => ({
          ...item,
          showMenu: false,
        })),
      );
    }
  };

  const convertDate = (value) => {
    const date = new Date(value);

    const day = date.getDate();
    const month = date.getMonth() + 1;

    const monthName = t(`months.${month}`);

    const formattedDate = `${day} ${monthName}`;

    return formattedDate;
  };
  const warningButton = () => {
    fetchDetails();
    fetchSub();
    fetchInvitation();
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [invitationData]);

  useEffect(() => {
    setInvitationInputToggle("");

    try {
      fetchDetails();
      fetchInvitation();
      fetchSub();
    } catch (err) {
      console.error("Error:", err);
    }
  }, []);

  return (
    <div className={`detail-page`}>
      <div className="details-container">
        {scheduleDetail.isOneTime ? null : (
          <div className="date">
            <b>{convertDate(eventDate)}</b>
          </div>
        )}

        <div className="sessions-week-days">
          {scheduleDetail.isOneTime ? (
            <h2>{convertDate(eventDate)}</h2>
          ) : (
            <>
              {scheduleDetail?.scheduleDays && (
                <Weekdays weekdaysData={scheduleDetail.scheduleDays} />
              )}
            </>
          )}

          <div className="details-description">
            <span>{scheduleDetail.description}</span>
            <div className="deatils-menu">
              <DotsMenu
                menuItems={menuItems}
                isMenuOpen={detailMenuToggle}
                toggleMenu={() => handleToggleDetailsMenu()}
              />
            </div>
          </div>
        </div>

        {invitationData.length > 0 ? (
          <div className={`sub-details-container ${invitationInputToggle}`}>
            {invitationData.map((item, index) => {
              return (
                <div
                  className="single-sub"
                  key={item.id}
                  onClick={() => {
                    getDataId(item.id);
                    setPlayerDetailsToggle(true);
                    setPlayerDetails(item);
                  }}
                >
                  <div className="single-sub-name">
                    {item.consumerName ? (
                      <h3>{item.consumerName}</h3>
                    ) : item.phoneNumber ? (
                      <h3>{item.phoneNumber}</h3>
                    ) : (
                      <h3>{item.email}</h3>
                    )}
                  </div>
                  <div
                    className="single-sub-icons"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <div className="single-sub-status">
                      <span>{item.status}</span>
                    </div>
                    <div className="details-settings">
                      <DotsMenu
                        menuItems={[
                          {
                            text: t("cancel_invitation"),
                            onClick: () =>
                              handleMenuButton("cancel invitation"),
                          },
                          {
                            text: t("player_details"),
                            onClick: () =>
                              handleMenuButton("player details", item),
                          },
                        ]}
                        isMenuOpen={item.showMenu}
                        toggleMenu={() => toggleMenu("invitation", index)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          subDetails.length === 0 && (
            <div className={`empty-data ${invitationInputToggle}`}>
              {t("details_dont_have_subscription")}
            </div>
          )
        )}

        {subDetails.length > 0 ? (
          <div className={`sub-details-container ${invitationInputToggle}`}>
            {subDetails.map((item, index) => {
              return (
                <div
                  className="single-sub"
                  key={item.id}
                  onClick={() => {
                    getDataId(item.id);
                    setPayerIdentifier(item);
                    setPlayerDetailsToggle(true);
                    setPlayerDetails(item);
                  }}
                >
                  <div className="single-sub-name">
                    <h3>{item.businessAccountName}</h3>
                  </div>
                  <div
                    className="single-sub-icons"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {scheduleDetail?.financialDetails?.payerOption ===
                    "Group" ? (
                      <>
                        {item.isGroupPayer === true ? (
                          <div className="group-payer">
                            <PayerBadge />
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    {item.overduePayment === true ? (
                      <div className="group-payer">
                        <DuePayment />
                      </div>
                    ) : null}

                    {scheduleDetail?.financialDetails?.payerOption ===
                    "Group" ? (
                      <div className="details-settings">
                        <DotsMenu
                          menuItems={[
                            {
                              text: t("cancel_subscription"),
                              onClick: () =>
                                handleMenuButton("cancel subscription"),
                            },

                            {
                              text: t("select_as_group_payer"),
                              disabled:
                                payerIdentifier.isGroupPayer === true
                                  ? true
                                  : false,
                              onClick: () =>
                                handleMenuButton("select as group payer", item),
                            },
                            {
                              text: t("player_details"),
                              onClick: () =>
                                handleMenuButton("player details", item),
                            },
                          ]}
                          isMenuOpen={item.showMenu}
                          toggleMenu={() => toggleMenu("subscription", index)}
                        />
                      </div>
                    ) : (
                      <div className="details-settings">
                        <DotsMenu
                          menuItems={[
                            {
                              text: t("cancel_subscription"),
                              onClick: () =>
                                handleMenuButton("cancel subscription"),
                            },
                            {
                              text: t("player_details"),
                              onClick: () =>
                                handleMenuButton("player details", item),
                            },
                          ]}
                          isMenuOpen={item.showMenu}
                          toggleMenu={() => toggleMenu("subscription", index)}
                        />
                      </div>
                    )}
                  </div>{" "}
                </div>
              );
            })}
          </div>
        ) : null}

        <ModalConfirmation
          modalText={modalText}
          modalSubText={modalSubText}
          changeModal={setConfirmModalToggle}
          modalValue={confirmModalToggle}
          confirm={modalValue === 4 ? handleGroupPayer : postInvitation}
        />

        <ModalWarning
          warningText={warningModalText}
          buttonText={warningButtonText}
          changeModal={setWarningModalToggle}
          modalValue={warningModalToggle}
          Image={Tick}
          handleButton={() => {
            warningButton();
            if (modalValue === 1) {
              navigate("/sessions");
            }
          }}
        />

        {playerDetailsToggle && (
          <PlayerDetails
            playerName={playerDetails.businessAccountName}
            number={playerDetails.phoneNumber}
            email={playerDetails.email}
            changeModal={setPlayerDetailsToggle}
          />
        )}
      </div>

      <GlobalActionButtons
        negativeAction={() => {
          navigate(-1);
        }}
        positiveAction={() => {
          handleValidations();
        }}
        negativButtonText={t(
          invitationInputToggle === "invite" ? "cancel" : "back",
        )}
        positiveButtonText={t("invite_customer")}
        negativButtonBgColor={"var(--negative_bg_light_color)"}
        positiveButtonBgColor={"var(--positive_bg_color)"}
        negativButtonTextColor={"#000"}
        positiveButtonTextColor={"#fff"}
        disability={scheduleDetail.availableSeats > 0 ? false : true}
      />

      {loading && <Loader />}
    </div>
  );
};

export default SessionDetails;
